import { country } from "@features/country/data";
import { trackRequestProduct } from "@features/products/activities";
import {
  type CalculateCPARequestData,
  type CreateProductRequestData,
  type HighlightedProductsEntity,
  HighlightGroups,
  type ListCategoryGroupedVariantsParams,
  type ListHighlightedVariantsParams,
  type ListProductVariantsParams,
  PreorderType,
  type ProductDetailsEntity,
  type ProductEntity,
  type ProductLandingPageSettingsEntity,
  type ProductsRepositoryContract,
  type ProductsSearchParams,
  type UpdateLandingPageSettingsData,
} from "@features/products/domain";
import { BaseRepository } from "@features/shared/data";
import { type CategoryModel, mapCategory } from "../../../categories/data";
import type { CategoryEntity } from "../../../categories/domain";
import { productsApiUrls } from "../constants";
import { mapHighlightedProducts, mapProduct } from "../mappers";
import { mapLandingPageSettings } from "../mappers/landing-page-settings.mapper";
import { mapProductDetails } from "../mappers/product-details.mapper";
import type {
  CalculateCPAResponseModel,
  ListHighlightedVariantsResponseModel,
  ProductDetailsModel,
  ProductVariantModel,
} from "../models";
import type { ProductLandingPageSettingsModel } from "../models/landing-page-settings.model";

export class ProductsRepository
  extends BaseRepository
  implements ProductsRepositoryContract
{
  public async getNewIdByLegacyId(
    legacyId: string,
    type: "product" | "variant",
  ): Promise<number> {
    const { data: newId } = await this.http.get<number>(
      productsApiUrls.GET_NEW_ID_BY_LEGACY_ID,
      { params: { legacyId, type } },
    );

    return newId;
  }

  /**
   * Post a request for a new product
   */
  public async createProductRequest(
    data: CreateProductRequestData,
  ): Promise<void> {
    await this.http.post<CreateProductRequestData, void>(
      productsApiUrls.REQUEST_PRODUCT_URL,
      data,
    );

    trackRequestProduct(data);
  }

  public async calculateCPA(data: CalculateCPARequestData): Promise<number> {
    const response = await this.http.post<
      CalculateCPARequestData,
      { data: CalculateCPAResponseModel }
    >(productsApiUrls.GET_CALCULATE_CPA_URL(data.productId), data);

    return response.data.data.cpa;
  }

  /**
   * List highlighted products
   */
  public async listHighlighted({
    highlightGroups,
    ...params
  }: ListHighlightedVariantsParams): Promise<HighlightedProductsEntity> {
    const response = await this.http.get<ListHighlightedVariantsResponseModel>(
      productsApiUrls.GET_HIGHLIGHTED_VARIANTS_URL,
      {
        params: {
          ...params,
          highlightGroups: highlightGroups.join(","),
        },
      },
    );

    return mapHighlightedProducts(response.data);
  }

  /**
   * List products for the given categories
   */
  public async listByCategories({
    categoryIds,
    ...params
  }: ListCategoryGroupedVariantsParams): Promise<CategoryEntity[]> {
    const response = await this.http.get<CategoryModel[]>(
      productsApiUrls.GET_VARIANT_GROUPS_BY_CATEGORY_URL,
      {
        params: {
          ...params,
          categoryIds: categoryIds.join(","),
        },
      },
    );

    return response.data.map(mapCategory);
  }

  /**
   * Search products
   */
  public async search(params: ProductsSearchParams): Promise<ProductEntity[]> {
    const response = await this.http.get<ProductVariantModel[]>(
      productsApiUrls.GET_PRODUCTS_SEARCH_URL,
      {
        params,
      },
    );

    return response.data.map(mapProduct);
  }

  /**
   * List open for testing products
   */
  public async listOpenForTesting(
    params?: ProductsSearchParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      highlightGroup: HighlightGroups.OPEN_FOR_TESTING,
    });
  }

  /**
   * List offers products
   */
  public async listOffers(
    params?: ProductsSearchParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      highlightGroup: HighlightGroups.OFFERS,
    });
  }

  /**
   * List new arrivals products
   */
  public async listNewArrivals(
    params?: ProductsSearchParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      highlightGroup: HighlightGroups.NEW_ARRIVALS,
    });
  }

  /**
   * List best seller products
   */
  public async listBestSeller(
    params?: ProductsSearchParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      highlightGroup: HighlightGroups.BEST_SELLERS,
    });
  }

  public async listFavorites(
    params?: ProductsSearchParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      isFavourite: true,
    });
  }

  /**
   * List locked on you products
   */
  public async listLockedOnYou(
    params?: ProductsSearchParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      highlightGroup: HighlightGroups.LOCKED_PRODUCTS,
    });
  }

  /**
   * List products by category
   */
  public async listProductsByCategory(
    params: ListProductVariantsParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants(params);
  }

  /**
   * List preorder products
   */
  public async listInternalPreorderProducts(
    params: ListProductVariantsParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      preorderType: PreorderType.INTERNAL,
    });
  }

  public async listExternalPreorderProducts(
    params: ListProductVariantsParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      preorderType: PreorderType.EXTERNAL,
    });
  }

  /**
   * List variants, a general listing products API
   */
  private async listVariants(
    params: ListProductVariantsParams,
  ): Promise<ProductEntity[]> {
    const response = await this.http.get<ProductVariantModel[]>(
      productsApiUrls.GET_PRODUCT_VARIANTS_URL,
      {
        params,
      },
    );

    return response.data.map(mapProduct);
  }

  /**
   * List discount on quantities products
   */
  public async listDiscountOnQuantities(
    params?: ProductsSearchParams,
  ): Promise<ProductEntity[]> {
    return this.listVariants({
      ...params,
      highlightGroup: HighlightGroups.DISCOUNT_ON_QUANTITIES,
    });
  }

  public async getProductDetails(
    productId: number,
  ): Promise<ProductDetailsEntity> {
    const response = await this.http.get<ProductDetailsModel>(
      productsApiUrls.GET_PRODUCT_DETAILS_URL(productId),
    );

    return mapProductDetails(response.data);
  }

  public async getLandingPageSettings(
    sku: string,
  ): Promise<ProductLandingPageSettingsEntity> {
    return this.http
      .get<{
        data: ProductLandingPageSettingsModel;
      }>(productsApiUrls.GET_LANDING_PAGE_SETTINGS_URL(sku))
      .then(res => {
        return mapLandingPageSettings(res.data.data);
      });
  }

  public async enableLandingPage(sku: string, price: number): Promise<void> {
    await this.http.post(productsApiUrls.ENABLE_LANDING_PAGE_URL, {
      country: country.code,
      price,
      productId: sku,
    });
  }

  public async updateLandingPageSettings(
    productId: string,
    data: ProductLandingPageSettingsEntity,
  ): Promise<void> {
    const { media, ...rest } = data;

    const payload: UpdateLandingPageSettingsData = {
      ...rest,
      country: country.code,
      productPicturesURLs: media,
    };

    await this.http.patch(
      productsApiUrls.UPDATE_LANDING_PAGE_SETTINGS_URL(productId),
      payload,
    );
  }

  public async uploadProductMedia(mediaFile: File): Promise<string> {
    const formData = new FormData();
    formData.append("image", mediaFile);

    const result = await this.http.post<FormData, { filePath: string }>(
      productsApiUrls.UPLOAD_PRODUCT_MEDIA_URL,
      formData,
    );

    return result.data.filePath;
  }
}
